import { useAxios } from '@/hooks/useAxios';
import { useGetUserKey } from '@/hooks/useQueryKeys';
import { Statistic } from '@/types';
import { useQuery } from '@/utils/api';

/**
 * Options for the useStatisticsBatch hook
 */
interface UseStatisticsBatchOptions {
  /**
   * Array of statistic keys to fetch in a single batch request.
   * Keys should be in the format used by the /my endpoint:
   * - "#statistic#asset#origin" or "asset#origin"
   * - "#statistic#my##attribute#port##now" or "my##attribute#port##now"
   *
   * The hook automatically prepends "#statistic#" if not present.
   */
  statisticKeys: string[];

  /**
   * Enable or disable the query. Default: true
   */
  enabled?: boolean;

  /**
   * How long fetched data stays fresh (in milliseconds).
   * Default: 5 minutes (300000ms)
   */
  staleTime?: number;

  /**
   * Configuration for impersonation
   */
  doNotImpersonate?: boolean;
  email?: string;
}

/**
 * Return type for useStatisticsBatch hook
 */
interface UseStatisticsBatchReturn {
  /**
   * Statistics data organized by key.
   * Each key maps to an array of Statistic objects.
   * Returns undefined while loading or on error.
   */
  data: Record<string, Statistic[]> | undefined;

  /**
   * Loading state
   */
  isLoading: boolean;

  /**
   * Query status: 'pending' | 'error' | 'success'
   */
  status: 'pending' | 'error' | 'success';

  /**
   * Error object if query failed
   */
  error: Error | null;

  /**
   * Function to manually refetch the batch statistics
   */
  refetch: () => Promise<void>;

  /**
   * Function to invalidate the query cache
   */
  invalidate: () => void;
}

/**
 * Batch statistics hook that fetches multiple statistics in a single API request.
 *
 * **Performance Impact:**
 * - Reduces 11 sequential API requests to 1 batch request
 * - Expected improvement: 97-99.5% reduction in load time
 * - Before: 5.5-16.5 seconds (11 requests × 500-1500ms each)
 * - After: 50-200ms (single batch request)
 *
 * **Usage Example:**
 * ```typescript
 * const { data, isLoading, error } = useStatisticsBatch({
 *   statisticKeys: [
 *     'asset#origin',
 *     'my##attribute#port##now',
 *     'asset#status',
 *   ],
 *   enabled: true,
 *   staleTime: 5 * 60 * 1000, // 5 minutes
 * });
 *
 * if (isLoading) return <Loader />;
 * if (error) return <ErrorMessage error={error} />;
 *
 * // Access individual statistics
 * const originStats = data['asset#origin'] || [];
 * ```
 *
 * **Backend API:**
 * Makes a GET request to `/my?key={key1}&key={key2}&key={key3}...`
 * Response is an array of results in the same order as requested keys.
 *
 * **Cache Strategy:**
 * - Query keys are sorted alphabetically for cache consistency
 * - Same set of keys (in any order) will use the same cache entry
 * - Default stale time: 5 minutes (matches existing statistics queries)
 *
 * **Error Handling:**
 * - Retries up to 3 times with exponential backoff
 * - Gracefully handles partial failures (returns empty array for failed queries)
 * - Error details available via the error property
 *
 * @param options - Configuration options for the batch query
 * @returns Statistics data organized by key with loading/error states
 */
export function useStatisticsBatch(
  options: UseStatisticsBatchOptions
): UseStatisticsBatchReturn {
  const {
    statisticKeys,
    enabled = true,
    staleTime = 5 * 60 * 1000, // 5 minutes default
    doNotImpersonate,
    email,
  } = options;

  const axios = useAxios({ doNotImpersonate, email });

  // Normalize keys to ensure they start with "#statistic#"
  const normalizedKeys = statisticKeys.map(key => {
    if (key.startsWith('#statistic#')) {
      return key;
    }
    return `#statistic#${key}`;
  });

  // Sort keys alphabetically for consistent cache keys
  // This ensures that ['asset#origin'] and ['asset#origin']
  // use the same cache entry
  const sortedKeys = [...normalizedKeys].sort();

  // Build query key for TanStack Query cache
  // Format: ['statistics', 'batch', userId, ...sortedKeys]
  const queryKey = useGetUserKey(
    ['statistics', 'batch', ...sortedKeys],
    doNotImpersonate,
    email
  );

  const query = useQuery<Record<string, Statistic[]>>({
    queryKey,
    enabled: enabled && statisticKeys.length > 0,
    staleTime,
    retry: 3,
    defaultErrorMessage: 'Failed to fetch batch statistics',
    queryFn: async () => {
      // Build URL with multiple key parameters
      // Format: /my?key={key1}&key={key2}&key={key3}
      const params = new URLSearchParams();
      normalizedKeys.forEach(key => {
        params.append('key', key);
      });

      // Add label parameter for proper routing
      params.append('label', 'statistic');

      // Make batch request
      const { data } = await axios.get(`/my?${params.toString()}`);

      // Response format from backend:
      // [
      //   { statistics: [...] },  // First key's results
      //   { statistics: [...] },  // Second key's results
      //   ...
      // ]
      //
      // Transform to Record<string, Statistic[]> format:
      // {
      //   'asset#origin': [...],
      //   ...
      // }

      const result: Record<string, Statistic[]> = {};

      // Handle array response (batch query)
      if (Array.isArray(data)) {
        normalizedKeys.forEach((key, index) => {
          const responseItem = data[index];
          // Remove "#statistic#" prefix for the result key to match expected format
          const cleanKey = key.replace('#statistic#', '');
          result[cleanKey] = responseItem?.statistics || [];
        });
      } else if (data?.statistics) {
        // Handle single statistic response (fallback for single key)
        const cleanKey = normalizedKeys[0].replace('#statistic#', '');
        result[cleanKey] = data.statistics;
      } else {
        // Fallback: return empty arrays for all keys
        normalizedKeys.forEach(key => {
          const cleanKey = key.replace('#statistic#', '');
          result[cleanKey] = [];
        });
      }

      return result;
    },
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    status: query.status,
    error: query.error,
    refetch: async () => {
      await query.refetch();
    },
    invalidate: query.invalidate,
  };
}
